import * as styles from "./MonthPickerGrid.css";

import type { JSX, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { addMonths } from "@frontend/components/atoms/DatePicker/dateMath";
import { useLingui } from "@lingui/react/macro";

import { useMonthPickerContext } from "./MonthPicker.context";
import { MonthPickerMonth } from "./MonthPickerMonth";
import { MONTHS_PER_ROW } from "./constants";
import { type CalendarMonth, compareMonth, thisMonth } from "./monthMath";

const MONTHS = 12;

function defaultActiveMonth(
  viewYear: number,
  selected: CalendarMonth | null,
): CalendarMonth {
  if (selected && selected.year === viewYear) {
    return selected;
  }

  const now = thisMonth();

  if (now.year === viewYear) {
    return now;
  }

  return { year: viewYear, month: 0 };
}

export function MonthPickerGrid(): JSX.Element {
  const { t } = useLingui();

  const { viewYear, selected, min, max, goToView } = useMonthPickerContext();

  const gridRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<CalendarMonth | null>(null);

  const [activeMonth, setActiveMonth] = useState<CalendarMonth>(() =>
    defaultActiveMonth(viewYear, selected),
  );

  useEffect(() => {
    if (pendingFocusRef.current) {
      const pending = pendingFocusRef.current;

      pendingFocusRef.current = null;
      setActiveMonth(pending);
      focusMonth(pending);

      return;
    }

    setActiveMonth(defaultActiveMonth(viewYear, selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewYear]);

  function focusMonth({ year, month }: CalendarMonth) {
    gridRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-year="${year}"][data-month="${month}"]`,
      )
      ?.focus({ preventScroll: true });
  }

  function navigateTo(next: CalendarMonth) {
    if (next.year === viewYear) {
      setActiveMonth(next);
      focusMonth(next);

      return;
    }

    pendingFocusRef.current = next;
    goToView(next.year);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const { year, month } = activeMonth;

    switch (event.key) {
      case "ArrowLeft":
        navigateTo(addMonths(year, month, -1));
        break;
      case "ArrowRight":
        navigateTo(addMonths(year, month, 1));
        break;
      case "ArrowUp":
        navigateTo(addMonths(year, month, -MONTHS_PER_ROW));
        break;
      case "ArrowDown":
        navigateTo(addMonths(year, month, MONTHS_PER_ROW));
        break;
      case "Home":
        navigateTo({ year, month: 0 });
        break;
      case "End":
        navigateTo({ year, month: MONTHS - 1 });
        break;
      case "PageUp":
        navigateTo({ year: year - 1, month });
        break;
      case "PageDown":
        navigateTo({ year: year + 1, month });
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  const now = thisMonth();

  const cells = Array.from({ length: MONTHS }, (_, month) => ({
    year: viewYear,
    month,
  }));

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={t`Months`}
      className={styles.grid}
      onKeyDown={handleKeyDown}
    >
      {Array.from({ length: MONTHS / MONTHS_PER_ROW }, (_, row) => (
        <div key={row} role="row" className={styles.row}>
          {cells
            .slice(row * MONTHS_PER_ROW, row * MONTHS_PER_ROW + MONTHS_PER_ROW)
            .map((cell) => (
              <MonthPickerMonth
                key={cell.month}
                year={cell.year}
                month={cell.month}
                selected={
                  null !== selected && compareMonth(cell, selected) === 0
                }
                current={compareMonth(cell, now) === 0}
                active={compareMonth(cell, activeMonth) === 0}
                disabled={
                  (null !== min && compareMonth(cell, min) < 0) ||
                  (null !== max && compareMonth(cell, max) > 0)
                }
              />
            ))}
        </div>
      ))}
    </div>
  );
}
